import type { MoneyLike } from "./billingFormat";

export interface ContractSummaryContract {
  currency: string;
  billingPeriodMonths: number;
  items: { type: string; amount?: MoneyLike | null }[];
}

export interface ContractSummaryPayment {
  id: string;
  invoiceDate: string;
  status: string;
  expired: boolean;
  amount: MoneyLike;
  invoice?: { number: string } | null;
}

export interface ContractSummary<P extends ContractSummaryPayment> {
  /** Лицензия за биллинг-период, делённая на число месяцев; null без лицензии. */
  monthlyLicense: MoneyLike | null;
  paid: MoneyLike;
  awaiting: MoneyLike;
  overdueCount: number;
  /** Ближайший платёж без счёта в draft или pending. */
  nextInvoice: P | null;
  /** Платежи с датой инвойса не позже сегодня, сначала новые. */
  recentPayments: P[];
}

export const RECENT_PAYMENTS_LIMIT = 5;

const AWAITING = new Set(["wait_payment", "on_way"]);
const UPCOMING = new Set(["draft", "pending"]);

const round2 = (value: number) => Math.round(value * 100) / 100;
const time = (value: string) => new Date(value).getTime();

/**
 * Сводка вкладки «Детали» контракта (спека 2026-10-16 §5). Суммы в
 * валюте контракта: платёж по контракту всегда в ней. Платежи приходят
 * одним запросом с сервера (до 100, у контракта их единицы-десятки).
 */
export function buildContractSummary<P extends ContractSummaryPayment>(
  contract: ContractSummaryContract,
  payments: P[],
  now: Date = new Date()
): ContractSummary<P> {
  const currency = contract.currency;
  const sum = (items: P[]) =>
    round2(
      items
        .filter((item) => item.amount.currency === currency)
        .reduce((total, item) => total + item.amount.value, 0)
    );

  const license = contract.items
    .filter((item) => item.type === "LICENSE")
    .reduce((total, item) => total + (item.amount?.value ?? 0), 0);
  const months = Math.max(1, contract.billingPeriodMonths);

  // ES2021 в lib кабинета: без Array.prototype.at.
  const nextInvoice =
    payments
      .filter((item) => UPCOMING.has(item.status) && !item.invoice)
      .sort(
        (left, right) => time(left.invoiceDate) - time(right.invoiceDate)
      )[0] ?? null;

  const recentPayments = payments
    .filter((item) => time(item.invoiceDate) <= now.getTime())
    .sort((left, right) => time(right.invoiceDate) - time(left.invoiceDate))
    .slice(0, RECENT_PAYMENTS_LIMIT);

  return {
    monthlyLicense:
      license > 0 ? { value: round2(license / months), currency } : null,
    paid: {
      value: sum(payments.filter((item) => item.status === "paid")),
      currency,
    },
    awaiting: {
      value: sum(payments.filter((item) => AWAITING.has(item.status))),
      currency,
    },
    overdueCount: payments.filter((item) => item.expired).length,
    nextInvoice,
    recentPayments,
  };
}
